import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Cancel as CancelIcon, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQueryClient } from 'react-query';

import { scheduleService, UpdateMatchRequest, ScheduleMatch } from '@/services/scheduleService';
import { invalidateMatchQueries } from '@/utils/calendarInvalidation';
import { emitMatchChange } from '@/utils/calendarEvents';

interface EditMatchDialogProps {
  open: boolean;
  onClose: () => void;
  match: ScheduleMatch | null;
}

const EditMatchDialog: React.FC<EditMatchDialogProps> = ({ open, onClose, match }) => {
  const [formData, setFormData] = useState<UpdateMatchRequest>({});
  const [dateTime, setDateTime] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string>('');

  const queryClient = useQueryClient();

  useEffect(() => {
    if (match && open) {
      setFormData({
        homeTeam: match.homeTeam,
        awayTeam: match.awayTeam,
        location: match.location || '',
        // status field removed - no longer editable
        homeScore: match.homeScore !== null ? match.homeScore : undefined,
        awayScore: match.awayScore !== null ? match.awayScore : undefined,
      });
      setDateTime(dayjs(match.dateTime));
      setError(''); // Clear any previous errors
    }
  }, [match, open]);

  const updateMatchMutation = useMutation(
    ({ matchId, updates }: { matchId: string; updates: UpdateMatchRequest }) =>
      scheduleService.updateMatch(matchId, updates),
    {
      onSuccess: (updatedMatch) => {
        // Use centralized invalidation utility
        invalidateMatchQueries(queryClient);
        
        // Emit event for global listeners
        emitMatchChange('updated', updatedMatch);
        
        handleClose();
      },
      onError: (error: any) => {
        setError(error.response?.data?.message || 'Failed to update match');
      },
    }
  );

  const handleClose = () => {
    setFormData({});
    setDateTime(null);
    setError('');
    onClose();
  };

  const handleSubmit = () => {
    if (!match) return;

    if (!formData.homeTeam || !formData.awayTeam || !dateTime) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.homeTeam === formData.awayTeam) {
      setError('Home and away teams must be different');
      return;
    }

    const updates: UpdateMatchRequest = {
      ...formData,
      dateTime: dateTime.toISOString(),
    };

    updateMatchMutation.mutate({ matchId: match.id, updates });
  };

  const handleInputChange = (field: keyof UpdateMatchRequest, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError('');
  };

  if (!match) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableAutoFocus>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon />Edit Match</Box></DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                required
                label="Home Team"
                value={formData.homeTeam || ''}
                onChange={(e) => handleInputChange('homeTeam', e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                required
                label="Away Team"
                value={formData.awayTeam || ''}
                onChange={(e) => handleInputChange('awayTeam', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <DateTimePicker
                label="Date & Time *"
                value={dateTime}
                onChange={setDateTime}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location || ''}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="Enter match location"
              />
            </Grid>

            {/* Status field removed - users can no longer edit match status */}

            {(match?.status === 'COMPLETED' || match?.status === 'IN_PROGRESS') && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Home Score"
                    type="number"
                    value={formData.homeScore !== undefined ? formData.homeScore : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const homeScore = value === '' ? null : parseInt(value) || 0;
                      
                      // If user deletes home score but away score exists, set home to 0
                      if (homeScore === null && formData.awayScore !== undefined && formData.awayScore !== null) {
                        handleInputChange('homeScore', 0);
                      } else {
                        handleInputChange('homeScore', homeScore ?? 0);
                        
                        // If user enters a home score and away score is empty, set away score to 0
                        if (homeScore !== null && (formData.awayScore === undefined || formData.awayScore === null)) {
                          handleInputChange('awayScore', 0);
                        }
                      }
                    }}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Away Score"
                    type="number"
                    value={formData.awayScore !== undefined ? formData.awayScore : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const awayScore = value === '' ? null : parseInt(value) || 0;
                      
                      // If user deletes away score but home score exists, set away to 0
                      if (awayScore === null && formData.homeScore !== undefined && formData.homeScore !== null) {
                        handleInputChange('awayScore', 0);
                      } else {
                        handleInputChange('awayScore', awayScore ?? 0);
                        
                        // If user enters an away score and home score is empty, set home score to 0
                        if (awayScore !== null && (formData.homeScore === undefined || formData.homeScore === null)) {
                          handleInputChange('homeScore', 0);
                        }
                      }
                    }}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={updateMatchMutation.isLoading}
            startIcon={updateMatchMutation.isLoading ? undefined : <SaveIcon />}
          >
            {updateMatchMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              'Update Match'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EditMatchDialog;
